import clsx from "clsx";
import { useTranslation } from "react-i18next";

const Button = ({
  variant = "primary",
  size = "md",
  color,
  disabled,
  loading,
  type = "button",
  className,
  children,
  leadingIcon: LeadingIcon,
  trailingIcon: TrailingIcon,
  ...props
}) => {
  const { t } = useTranslation();

  const primaryBlue =
    "border border-transparent bg-blue-600 text-white shadow-sm hover:bg-blue-700 focus:ring-blue-500";
  const secondaryGray =
    "border border-gray-300 bg-white text-gray-700 shadow-sm hover:bg-gray-50 focus:ring-blue-500";

  const styles = {
    base: "inline-flex items-center justify-center rounded-md font-medium focus:outline-none focus:ring-2 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed",

    variant: {
      primary: {
        _: primaryBlue,
        blue: primaryBlue,
        red: "border border-transparent bg-red-600 text-white shadow-sm hover:bg-red-700 focus:ring-red-500",
        green:
          "border border-transparent bg-green-600 text-white shadow-sm hover:bg-green-700 focus:ring-green-500",
        yellow:
          "border border-transparent bg-yellow-600 text-white shadow-sm hover:bg-yellow-700 focus:ring-yellow-500",
      },
      secondary: {
        _: secondaryGray,
        gray: secondaryGray,
        blue: "border border-blue-300 bg-white text-blue-700 shadow-sm hover:bg-blue-50 focus:ring-blue-500",
        red: "border border-red-300 bg-white text-red-700 shadow-sm hover:bg-red-50 focus:ring-red-500",
        green:
          "border border-green-300 bg-white text-green-700 shadow-sm hover:bg-green-50 focus:ring-green-500",
        yellow:
          "border border-yellow-300 bg-white text-yellow-700 shadow-sm hover:bg-yellow-50 focus:ring-yellow-500",
      },
    },

    size: {
      sm: "gap-x-1 px-3 py-1.5 text-xs",
      md: "gap-x-1.5 px-4 py-2 text-sm",
      lg: "gap-x-2 px-6 py-3 text-base",
    },

    icon: {
      sm: "h-4 w-4",
      md: "h-5 w-5",
      lg: "h-6 w-6",
    },
  };

  const variantStyle = styles.variant[variant][color || "_"];

  return (
    <button
      type={type}
      disabled={disabled || loading}
      className={clsx(styles.base, variantStyle, styles.size[size], className)}
      {...props}
    >
      {loading ? (
        <>
          <svg
            className={clsx("animate-spin -ml-1 mr-2", styles.icon[size])}
            fill="none"
            viewBox="0 0 24 24"
          >
            <circle
              className="opacity-25"
              cx="12"
              cy="12"
              r="10"
              stroke="currentColor"
              strokeWidth="4"
            />
            <path
              className="opacity-75"
              fill="currentColor"
              d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
            />
          </svg>
          {t("common.loading")}...
        </>
      ) : (
        <>
          {LeadingIcon && (
            <LeadingIcon
              className={clsx("-ml-0.5", styles.icon[size])}
              aria-hidden="true"
            />
          )}
          {children}
          {TrailingIcon && (
            <TrailingIcon
              className={clsx("-mr-0.5", styles.icon[size])}
              aria-hidden="true"
            />
          )}
        </>
      )}
    </button>
  );
};

export default Button;
